import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, MapPin, Search, ArrowRight, Euro, BedDouble, Bath, Maximize, Building2, CheckCircle, Heart, Key, Users, Clock, Star, Shield, Info } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { Helmet } from 'react-helmet';
import { getPropertyPriceLabel } from '../../../utils/propertyPrice';
import { PLACEHOLDER_IMAGE } from '../../../utils/imageProxy';

const CaseInVenditaScandicci = () => {
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        // Get published status
        const { data: publishedStatus } = await supabase
          .from('property_statuses')
          .select('id')
          .eq('name', 'published')
          .single();

        if (!publishedStatus) {
          throw new Error('Stato pubblicato non trovato');
        }

        // Fetch properties
        const { data, error } = await supabase
          .from('properties')
          .select(`
            *,
            zones:zone_id(*),
            property_images(*)
          `)
          .eq('status_id', publishedStatus.id)
          .eq('type', 'vendita')
          .eq('featured', true)
          .limit(6);
        
        if (error) throw error;
        
        // Transform data
        const transformedData = data?.map(property => ({
          ...property,
          primaryImage: property.property_images.find((img: any) => img.is_primary)?.url 
                      || property.property_images[0]?.url 
                      || PLACEHOLDER_IMAGE
        })) || [];
        
        setProperties(transformedData);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  // Zone data for Scandicci
  const scandicciZones = [
    {
      name: "Centro",
      description: "Il cuore pulsante di Scandicci, completamente rinnovato con la nuova piazza civica e servizi moderni.",
      priceRange: "€2.500 - €3.200",
      mainTypes: "Appartamenti moderni, uffici",
      features: ["Tramvia per Firenze", "Negozi e ristoranti", "Servizi comunali"]
    },
    {
      name: "Le Bagnese",
      description: "Area residenziale di prestigio, caratterizzata da immobili di qualità e ampi spazi verdi privati.",
      priceRange: "€2.800 - €3.400",
      mainTypes: "Ville, villette a schiera",
      features: ["Zone verdi", "Vicinanza a Firenze", "Residenze di prestigio"]
    },
    {
      name: "Casellina",
      description: "Quartiere ben servito che unisce zone residenziali e aree commerciali, in costante sviluppo.",
      priceRange: "€2.200 - €2.800",
      mainTypes: "Appartamenti, case a schiera",
      features: ["Supermercati", "Scuole", "Parchi"]
    },
    {
      name: "San Giusto",
      description: "Area in forte crescita, caratterizzata da costruzioni recenti e ottimi collegamenti.",
      priceRange: "€2.300 - €2.900",
      mainTypes: "Appartamenti nuovi, uffici",
      features: ["Edifici moderni", "Spazi commerciali", "Facilmente raggiungibile"]
    },
    {
      name: "Vingone",
      description: "Zona tranquilla e principalmente residenziale, ideale per famiglie che cercano quiete.",
      priceRange: "€2.100 - €2.600",
      mainTypes: "Appartamenti, villette",
      features: ["Aree verdi", "Ambiente familiare", "Servizi di quartiere"]
    },
    {
      name: "San Colombano",
      description: "Frazione che unisce il fascino della campagna toscana con servizi moderni e collegamenti rapidi.",
      priceRange: "€1.900 - €2.500",
      mainTypes: "Case indipendenti, rustici",
      features: ["Panorama collinare", "Ambiente tranquillo", "Prezzi accessibili"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Case in Vendita a Scandicci | Molinaro Immobiliare</title>
        <meta name="description" content="Scopri le migliori case in vendita a Scandicci. Appartamenti, ville e soluzioni per ogni esigenza con consulenza personalizzata dai nostri esperti." />
        <meta name="keywords" content="case in vendita scandicci, appartamenti scandicci, ville scandicci, immobili scandicci, acquistare casa scandicci, molinaro immobiliare" />
        <link rel="canonical" href="https://molinaroimmobiliare.it/case-in-vendita-scandicci" />
        <meta property="og:title" content="Case in Vendita a Scandicci | Molinaro Immobiliare" />
        <meta property="og:description" content="Scopri le migliori case in vendita a Scandicci. Appartamenti, ville e soluzioni per ogni esigenza con consulenza personalizzata dai nostri esperti." />
        <meta property="og:url" content="https://molinaroimmobiliare.it/case-in-vendita-scandicci" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://molinaroimmobiliare.it/molinaro.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Case in Vendita a Scandicci | Molinaro Immobiliare" />
        <meta name="twitter:description" content="Scopri le migliori case in vendita a Scandicci. Appartamenti, ville e soluzioni per ogni esigenza con consulenza personalizzata dai nostri esperti." />
        <meta name="twitter:image" content="https://molinaroimmobiliare.it/molinaro.png" />
        {/* Structured data for real estate */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "RealEstateAgent",
              "name": "Molinaro Immobiliare",
              "image": "https://molinaroimmobiliare.it/molinaro.png",
              "url": "https://molinaroimmobiliare.it",
              "telephone": "+39 327 8855115",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Via Pisana 327",
                "addressLocality": "Scandicci",
                "postalCode": "50018",
                "addressCountry": "IT"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 43.7667,
                "longitude": 11.1786
              },
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday"
                  ],
                  "opens": "09:00",
                  "closes": "19:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": "Saturday",
                  "opens": "09:00",
                  "closes": "13:00"
                }
              ],
              "areaServed": "Scandicci"
            }
          `}
        </script>
      </Helmet>

      <div className="bg-gray-50">
        {/* ATTENTION: Hero Section */}
        <section className="relative pt-28 pb-20 bg-gradient-to-r from-primary to-primary-dark overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://images.unsplash.com/photo-1613977257363-707ba9348227?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" 
              alt="Case in vendita a Scandicci" 
              className="w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
            />
          </div>
          <div className="absolute inset-0 bg-primary/70"></div>
          
          <div className="container mx-auto px-4 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto"
            >
              <span className="inline-block px-4 py-2 bg-secondary/20 backdrop-blur-sm text-white text-sm font-bold rounded-full mb-6">
                Le Migliori Proposte Immobiliari a Scandicci
              </span>
              <h1 className="text-5xl md:text-6xl font-monomakh text-white mb-6 leading-tight">
                Case in Vendita a Scandicci
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Scopri le migliori opportunità immobiliari in una delle zone più ricercate dell'area fiorentina.
                Appartamenti, ville e soluzioni per ogni esigenza con la consulenza dei nostri esperti locali.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                  <Home className="h-5 w-5 text-secondary mr-3" />
                  <span className="text-white">200+ case disponibili</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                  <MapPin className="h-5 w-5 text-secondary mr-3" />
                  <span className="text-white">Tutte le zone di Scandicci</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                  <Users className="h-5 w-5 text-secondary mr-3" />
                  <span className="text-white">Consulenti dedicati</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/immobili?tipo=vendita&zona=Scandicci"
                  className="bg-secondary hover:bg-secondary-light text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  <Search className="h-5 w-5 mr-2" />
                  Cerca Case a Scandicci
                </Link>
                <a
                  href="#contattaci"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  Consulenza Gratuita
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* INTEREST: Featured Properties Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Proposte Selezionate</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Case in Evidenza a Scandicci
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Ecco una selezione delle migliori proposte immobiliari attualmente disponibili a Scandicci.
                Soluzioni di qualità in posizioni strategiche, selezionate dai nostri esperti.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {loading ? (
                <div className="md:col-span-3 py-12 text-center">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                  <p className="mt-4 text-gray-600">Caricamento immobili...</p>
                </div>
              ) : properties.length === 0 ? (
                <div className="md:col-span-3 py-12 text-center">
                  <p className="text-lg text-gray-600">Nessun immobile in evidenza trovato.</p>
                  <Link to="/immobili?tipo=vendita&zona=Scandicci" className="mt-4 inline-block text-primary hover:underline">
                    Vedi tutti gli immobili disponibili
                  </Link>
                </div>
              ) : (
                properties.map((property, index) => (
                  <motion.div 
                    key={property.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group"
                  >
                    <Link to={`/immobile/${property.id}`} className="block h-full">
                      <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all h-full flex flex-col">
                        <div className="relative h-60 overflow-hidden">
                          <img
                            src={property.primaryImage}
                            alt={property.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                            width="400"
                            height="240"
                          />
                          <div className="absolute top-0 left-0 w-full p-3 flex justify-between items-start">
                            <span className="px-3 py-1.5 rounded-md text-sm font-bold bg-primary text-white">
                              Vendita
                            </span>
                            <button className="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors">
                              <Heart className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                          
                          <div className="absolute bottom-0 left-0 right-0">
                            <div className="bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                              <div className="flex items-center text-white">
                                <MapPin className="h-4 w-4 mr-1 text-secondary flex-shrink-0" />
                                <span className="text-sm font-medium truncate">{property.location}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-4 flex flex-col flex-grow">
                          <h3 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                            {property.title}
                          </h3>
                          
                          <div className="mt-auto">
                            <div className="grid grid-cols-3 gap-1 mb-3">
                              <div className="flex flex-col items-center p-1.5 bg-gray-50 rounded text-center">
                                <BedDouble className="h-4 w-4 text-primary mb-1" />
                                <span className="text-xs font-medium">{property.bedrooms} {property.bedrooms === 1 ? 'Locale' : 'Locali'}</span>
                              </div>
                              <div className="flex flex-col items-center p-1.5 bg-gray-50 rounded text-center">
                                <Bath className="h-4 w-4 text-primary mb-1" />
                                <span className="text-xs font-medium">{property.bathrooms} {property.bathrooms === 1 ? 'Bagno' : 'Bagni'}</span>
                              </div>
                              <div className="flex flex-col items-center p-1.5 bg-gray-50 rounded text-center">
                                <Maximize className="h-4 w-4 text-primary mb-1" />
                                <span className="text-xs font-medium">{property.size} m²</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-primary font-bold text-xl">
                                {getPropertyPriceLabel(property)}
                              </div>
                              
                              <span className="bg-secondary/10 hover:bg-secondary text-secondary hover:text-white px-4 py-1.5 rounded-md text-sm font-semibold transition-colors inline-flex items-center">
                                Dettagli
                                <ArrowRight className="ml-1 h-3.5 w-3.5" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))
              )}
            </div>
            
            <div className="text-center mt-12">
              <Link
                to="/immobili?tipo=vendita&zona=Scandicci"
                className="inline-flex items-center bg-primary hover:bg-primary-light text-white font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                Vedi Tutte le Case in Vendita a Scandicci
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* DESIRE: Neighborhoods Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Zone di Scandicci</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Scopri i Quartieri di Scandicci
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Ogni zona di Scandicci ha il suo carattere e le sue peculiarità. Dai prezzi alle caratteristiche 
                urbanistiche, ecco una panoramica per orientarti nella scelta.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {scandicciZones.map((zone, index) => (
                <motion.div
                  key={zone.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all p-6 border-t-4 border-primary"
                >
                  <h3 className="text-xl font-semibold text-primary mb-3">{zone.name}</h3>
                  <p className="text-gray-600 mb-4">{zone.description}</p>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                      <span className="text-sm text-gray-500">Prezzo al m²</span>
                      <span className="font-semibold text-primary">{zone.priceRange}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                      <span className="text-sm text-gray-500">Tipologie</span>
                      <span className="font-semibold text-primary">{zone.mainTypes}</span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Caratteristiche</h4>
                    <div className="grid grid-cols-1 gap-1">
                      {zone.features.map((feature, i) => (
                        <div key={i} className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0 mr-2" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Link 
                    to={`/immobili?tipo=vendita&zona=${zone.name}, Scandicci`} 
                    className="mt-2 inline-flex items-center text-secondary hover:text-secondary-light transition-colors text-sm font-medium"
                  >
                    Immobili in {zone.name}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Link
                to="/immobili?tipo=vendita&zona=Scandicci"
                className="inline-flex items-center bg-secondary hover:bg-secondary-light text-white font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                Esplora Tutte le Zone di Scandicci
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* DESIRE: Advantages Section */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 rounded-br-full bg-primary/5"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 rounded-tl-full bg-secondary/5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Perché Scegliere Scandicci</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Vivere a Scandicci: Tutti i Vantaggi
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Scandicci offre un perfetto equilibrio tra qualità della vita, servizi e collegamenti strategici.
                Scopri perché sempre più persone scelgono di acquistare casa in questa zona.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-primary"
              >
                <MapPin className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold text-primary mb-4">Posizione Strategica</h3>
                <p className="text-gray-600 mb-4">
                  A soli 7 km dal centro di Firenze, Scandicci offre la tranquillità della vita in provincia con tutti i vantaggi
                  della vicinanza alla città. Collegamenti eccellenti grazie alla tramvia.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">15 minuti di tramvia da Firenze</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Accesso rapido alla FI-PI-LI</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Perfetto per i pendolari</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-secondary"
              >
                <Building2 className="h-12 w-12 text-secondary mb-6" />
                <h3 className="text-xl font-semibold text-secondary mb-4">Qualità della Vita</h3>
                <p className="text-gray-600 mb-4">
                  Ampi spazi verdi, servizi di qualità e un contesto urbanistico rinnovato rendono Scandicci
                  un luogo ideale per famiglie e per chi cerca un ambiente vivibile e ben organizzato.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Parchi e aree verdi diffuse</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Scuole di ogni ordine e grado</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Centri sportivi e ricreativi</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-primary"
              >
                <Euro className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold text-primary mb-4">Investimento Sicuro</h3>
                <p className="text-gray-600 mb-4">
                  Il mercato immobiliare di Scandicci ha mostrato una costante crescita negli ultimi anni,
                  rendendo l'acquisto di una casa qui una scelta sicura anche per chi cerca un investimento.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Prezzi più competitivi rispetto a Firenze</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Costante rivalutazione degli immobili</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 ml-2">Alta richiesta di affitti per studenti e lavoratori</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* DESIRE: Testimonials Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Esperienze Reali</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Chi Ha Già Comprato Casa a Scandicci
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Le storie di chi ha scelto di vivere a Scandicci e ha trovato la casa dei propri sogni
                con l'aiuto dei nostri consulenti immobiliari.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg relative"
              >
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                    <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                  </svg>
                </div>
                
                <div className="flex space-x-1 mb-4 text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-600 mb-8 italic leading-relaxed">
                  "Sognavamo una casa con giardino a pochi minuti da Firenze e l'abbiamo trovata a Scandicci. 
                  La consulenza di Molinaro Immobiliare è stata fondamentale: conoscono ogni angolo della zona 
                  e ci hanno fatto risparmiare tanto tempo."
                </p>
                
                <div className="flex items-center">
                  <img 
                    src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80" 
                    alt="Testimonianza cliente" 
                    className="w-12 h-12 rounded-full object-cover mr-4"
                    width="48"
                    height="48"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="font-semibold text-primary">Fabio Bianchi</h4>
                    <p className="text-gray-500 text-sm">Ha acquistato a Le Bagnese</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg relative"
              >
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                    <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                  </svg>
                </div>
                
                <div className="flex space-x-1 mb-4 text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-600 mb-8 italic leading-relaxed">
                  "Cercavamo un trilocale luminoso in centro e abbiamo trovato la casa perfetta. Stupiti 
                  dalla velocità del processo: due settimane dalla visita alla proposta accettata. La tramvia 
                  per Firenze sotto casa è una comodità incredibile!"
                </p>
                
                <div className="flex items-center">
                  <img 
                    src="https://images.unsplash.com/photo-1597223557154-721c1cecc4b0?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80" 
                    alt="Testimonianza cliente" 
                    className="w-12 h-12 rounded-full object-cover mr-4"
                    width="48"
                    height="48"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="font-semibold text-primary">Giulia e Marco</h4>
                    <p className="text-gray-500 text-sm">Hanno acquistato in Centro</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white p-8 rounded-lg shadow-lg relative"
              >
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                    <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                  </svg>
                </div>
                
                <div className="flex space-x-1 mb-4 text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-600 mb-8 italic leading-relaxed">
                  "Abbiamo scelto un appartamento in una palazzina nuova a San Giusto e non potevamo 
                  fare scelta migliore. Prezzi più accessibili di Firenze ma con tutti i servizi a portata 
                  di mano. I consulenti di Molinaro sono stati magnifici!"
                </p>
                
                <div className="flex items-center">
                  <img 
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80" 
                    alt="Testimonianza cliente" 
                    className="w-12 h-12 rounded-full object-cover mr-4"
                    width="48"
                    height="48"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="font-semibold text-primary">Laura Neri</h4>
                    <p className="text-gray-500 text-sm">Ha acquistato a San Giusto</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ACTION: Contact Section */}
        <section id="contattaci" className="py-24 bg-primary">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl shadow-xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12">
                  <span className="text-secondary uppercase tracking-wider font-semibold">Contattaci</span>
                  <h2 className="text-3xl md:text-4xl font-monomakh text-primary mt-2 mb-6">
                    Hai Trovato la Casa dei tuoi Sogni?
                  </h2>
                  <p className="text-gray-600 mb-8 text-lg">
                    Compila il modulo per ricevere maggiori informazioni sulle case in vendita a Scandicci
                    o per fissare una visita con uno dei nostri consulenti.
                  </p>
                  
                  <form className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome*</label>
                        <input
                          type="text"
                          id="name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                          placeholder="Il tuo nome"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                        <input
                          type="email"
                          id="email"
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                          placeholder="La tua email"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Telefono*</label>
                        <input
                          type="tel"
                          id="phone"
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                          placeholder="Il tuo numero di telefono"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-1">Sono interessato a</label>
                        <select
                          id="interest"
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        >
                          <option value="">Seleziona una opzione</option>
                          <option value="apartment">Appartamento</option>
                          <option value="villa">Villa</option>
                          <option value="townhouse">Villetta a schiera</option>
                          <option value="land">Terreno</option>
                          <option value="other">Altro</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Messaggio</label>
                      <textarea
                        id="message"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        placeholder="Descrivi brevemente le tue esigenze"
                      ></textarea>
                    </div>
                    
                    <div className="flex items-start">
                      <input
                        id="privacy"
                        type="checkbox"
                        className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary mt-1"
                        required
                      />
                      <label htmlFor="privacy" className="ml-2 text-sm text-gray-500">
                        Ho letto e accetto la <Link to="/privacy-policy" className="text-secondary hover:underline">Privacy Policy</Link>*
                      </label>
                    </div>
                    
                    <div>
                      <button
                        type="submit"
                        className="w-full md:w-auto bg-secondary hover:bg-secondary-light text-white font-semibold py-3 px-8 rounded-lg transition-colors flex items-center justify-center"
                      >
                        Invia Richiesta
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </button>
                    </div>
                  </form>
                </div>
                
                <div className="bg-gray-900 p-8 md:p-12 text-white flex flex-col">
                  <h3 className="text-2xl font-semibold mb-6">Contattaci Direttamente</h3>
                  
                  <div className="space-y-6 mb-auto">
                    <div className="flex items-start">
                      <MapPin className="h-6 w-6 text-secondary mt-1 flex-shrink-0" />
                      <div className="ml-4">
                        <p className="font-semibold mb-1">Vieni a trovarci</p>
                        <p className="text-gray-300">Via Pisana 327, 50018 Scandicci (FI)</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-secondary mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <div className="ml-4">
                        <p className="font-semibold mb-1">Chiamaci</p>
                        <p className="text-gray-300">+39 327 8855115</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-secondary mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div className="ml-4">
                        <p className="font-semibold mb-1">Scrivici</p>
                        <p className="text-gray-300">info@molinaroimmobiliare.it</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Clock className="h-6 w-6 text-secondary mt-1 flex-shrink-0" />
                      <div className="ml-4">
                        <p className="font-semibold mb-1">Orari di apertura</p>
                        <p className="text-gray-300">Lun-Ven: 9:00-19:00</p>
                        <p className="text-gray-300">Sabato: 9:00-13:00</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-10">
                    <div className="p-4 bg-primary rounded-lg border border-primary-dark text-sm">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-secondary mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold mb-2">I nostri consulenti esperti di Scandicci</p>
                          <p className="text-gray-300">Contatta direttamente Andrea o Marco, i nostri specialisti delle zone di Scandicci, per una consulenza personalizzata.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Domande Frequenti</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Tutto Quello che Devi Sapere
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Ecco le risposte alle domande più frequenti sull'acquisto di case a Scandicci.
                Non trovi la risposta che cerchi? Contattaci!
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-6 md:gap-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h3 className="text-xl font-semibold text-primary mb-3 flex items-start">
                  <Shield className="h-6 w-6 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                  Quali sono i vantaggi di comprare casa a Scandicci rispetto a Firenze?
                </h3>
                <p className="text-gray-600 ml-9 mb-8">
                  Scandicci offre prezzi più competitivi (circa 20-30% inferiori), migliore qualità della vita con più 
                  spazi verdi, collegamenti rapidi con Firenze grazie alla tramvia, e un tessuto urbano rinnovato e moderno.
                </p>
                
                <h3 className="text-xl font-semibold text-primary mb-3 flex items-start">
                  <Shield className="h-6 w-6 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                  Quanto tempo ci vuole in media per vendere casa a Scandicci?
                </h3>
                <p className="text-gray-600 ml-9 mb-8">
                  Attualmente, il tempo medio di vendita a Scandicci è di circa 45-60 giorni per immobili correttamente valutati 
                  e presentati sul mercato. Le zone centrali e quelle ben collegate con la tramvia registrano tempi di vendita ancora più rapidi.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-xl font-semibold text-primary mb-3 flex items-start">
                  <Shield className="h-6 w-6 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                  Quali documenti servono per acquistare casa a Scandicci?
                </h3>
                <p className="text-gray-600 ml-9 mb-8">
                  Serviranno un documento d'identità valido, codice fiscale, eventuale documentazione relativa al mutuo 
                  se richiesto, e la caparra confirmatoria al momento della proposta. I nostri consulenti ti guideranno 
                  in ogni fase, facilitando anche la verifica della documentazione tecnica dell'immobile.
                </p>
                
                <h3 className="text-xl font-semibold text-primary mb-3 flex items-start">
                  <Shield className="h-6 w-6 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                  Quali sono le zone di Scandicci con il miglior rapporto qualità-prezzo?
                </h3>
                <p className="text-gray-600 ml-9 mb-8">
                  Casellina e San Giusto offrono attualmente il miglior rapporto qualità-prezzo, con immobili di buona 
                  qualità a prezzi ancora accessibili. La zona di Vingone sta crescendo molto per la sua posizione 
                  strategica e la disponibilità di servizi, mentre il Centro garantisce il massimo della comodità ma con prezzi più elevati.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SEO Content Section - Hidden visually but available for search engines */}
        <section aria-hidden="true">
          <div className="container mx-auto px-4 py-8">
            <div className="prose max-w-none mx-auto">
              <h2>Case in Vendita a Scandicci - Mercato Immobiliare</h2>
              <p>
                Cerchi casa in vendita a Scandicci? Molinaro Immobiliare offre un'ampia selezione di appartamenti, 
                ville e soluzioni abitative in tutte le zone di Scandicci. Il mercato immobiliare di Scandicci è 
                particolarmente dinamico grazie alla vicinanza con Firenze e agli ottimi collegamenti tramite tramvia.
              </p>
              <p>
                La nostra agenzia immobiliare a Scandicci propone case in vendita di varie tipologie, dalle soluzioni 
                più economiche agli immobili di lusso. Specializzati nel settore immobiliare a Scandicci da oltre 15 anni, 
                offriamo consulenze personalizzate per chi desidera comprare casa a Scandicci.
              </p>
              <p>
                Le zone più richieste per acquistare casa a Scandicci includono il Centro, Le Bagnese, Casellina, 
                San Giusto e Vingone. Ogni area ha caratteristiche diverse e prezzi variabili, con un prezzo medio 
                al metro quadro che oscilla tra i 2.000 e i 3.200 euro.
              </p>
              <p>
                Se stai pensando di comprare casa a Scandicci, affidati ai nostri agenti immobiliari esperti che 
                ti guideranno nella scelta dell'immobile più adatto alle tue esigenze. Vendi o compri casa a Scandicci? 
                Molinaro Immobiliare è il tuo partner immobiliare di fiducia.
              </p>
              <p>
                Contattaci per maggiori informazioni sulle case in vendita a Scandicci o per fissare una visita 
                agli immobili disponibili.
              </p>
              
              <h2>Appartamenti in Vendita a Scandicci</h2>
              <p>
                Gli appartamenti sono la tipologia di immobile più diffusa e richiesta a Scandicci. 
                Si trovano appartamenti di varie metrature, dai monolocali agli ampi pentavani, in costruzioni 
                che spaziano dagli edifici storici alle recenti realizzazioni architettoniche.
              </p>
              
              <h2>Ville e Case Indipendenti in Vendita a Scandicci</h2>
              <p>
                Per chi cerca spazi più ampi e privacy, Scandicci offre un'interessante selezione di ville e 
                case indipendenti, particolarmente nelle zone collinari e periferiche. Queste soluzioni sono 
                ideali per famiglie che desiderano godere di spazi esterni privati.
              </p>
              
              <h2>Il Mercato Immobiliare di Scandicci</h2>
              <p>
                Il mercato immobiliare di Scandicci ha registrato negli ultimi anni un costante apprezzamento, 
                grazie ai significativi interventi di riqualificazione urbana e al miglioramento dei collegamenti 
                con Firenze. Il valore degli immobili è cresciuto in modo stabile, rendendo l'acquisto di una casa 
                a Scandicci un investimento sicuro e con buone prospettive di rendimento.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CaseInVenditaScandicci;